import { getSection, FAMILY_LABELS } from '../utils';
import { formatNumberFr, formatPointsFr } from '@/lib/formatters';
import MarkdownText from '../MarkdownText';
import ComparativeRadar from './ComparativeRadar';

/**
 * Section II — Position globale.
 * Bloc dual : score Indice Benchory à gauche, lecture senior du cabinet
 * à droite. Puis radar comparatif (4 axes) + mini-cartes des 4 sous-scores.
 */
export default function GlobalPosition({ report }) {
  const section = getSection(report, 'global_position');
  const indice = report.indice_benchory;
  const indice_prev = report.indice_benchory_prev;
  const delta = indice != null && indice_prev != null
    ? Math.round((indice - indice_prev) * 10) / 10
    : null;
  const subscores = report.family_subscores || {};

  return (
    <section data-testid="global-position">
      <div className="text-[10px] text-cabinet-bordeaux uppercase tracking-[0.3em] font-medium mb-2">
        II — Position globale
      </div>
      <h2 className="font-playfair text-[22px] text-cabinet-ink mb-3">
        Votre position sur le mois écoulé
      </h2>
      <p className="font-playfair italic text-[14px] text-cabinet-ink-secondary mb-8 max-w-2xl">
        L'Indice Benchory traduit votre performance globale en un score 0 à 100 contextualisé sur votre segment.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <div
          className="bg-gradient-to-br from-cabinet-cream-dark to-cabinet-cream border border-cabinet-border p-10 flex flex-col items-center justify-center text-center"
          data-testid="indice-benchory-block"
        >
          <div className="text-[10px] text-cabinet-ink-secondary uppercase tracking-[0.25em] mb-4">
            Indice Benchory
          </div>
          <div className="font-playfair text-[64px] text-cabinet-bordeaux leading-none mb-1">
            {indice != null ? formatNumberFr(indice, 0) : '—'}
          </div>
          <div className="text-[12px] text-cabinet-ink-secondary mb-5">/ 100</div>
          {delta != null && (
            <div
              className={`inline-flex items-center gap-2 px-3 py-1 border text-[10px] font-medium uppercase tracking-[0.2em] ${
                delta > 0 ? 'border-cabinet-forest/40 text-cabinet-forest bg-cabinet-forest/5'
                : delta < 0 ? 'border-cabinet-bordeaux/40 text-cabinet-bordeaux bg-cabinet-bordeaux/5'
                : 'border-cabinet-border text-cabinet-ink-secondary'
              }`}
              data-testid="indice-delta-pill"
            >
              {formatPointsFr(delta, 1)} vs M-1
            </div>
          )}
        </div>

        <div className="bg-cabinet-paper border border-cabinet-border shadow-sm p-8" data-testid="senior-reading">
          <div className="text-[10px] text-cabinet-bordeaux uppercase tracking-[0.25em] mb-3 font-medium">
            Lecture senior · Cabinet
          </div>
          {section?.content_md ? (
            <MarkdownText text={section.content_md} className="text-[13.5px] text-cabinet-ink leading-[1.75]" />
          ) : (
            <p className="font-playfair italic text-[13px] text-cabinet-ink-soft leading-[1.85]">
              La lecture senior sera disponible dès la finalisation du prompt système maître et la génération complète du rapport.
            </p>
          )}
        </div>
      </div>

      <div className="bg-cabinet-paper border border-cabinet-border shadow-sm p-8 mb-8">
        <div className="text-[10px] text-cabinet-bordeaux uppercase tracking-[0.25em] font-medium mb-3">
          Analyse comparative
        </div>
        <h3 className="font-playfair text-[18px] text-cabinet-ink mb-6">
          Vous versus benchmark national
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-10 items-center">
          <div className="flex justify-center">
            <ComparativeRadar subscores={subscores} />
          </div>
          <ul className="space-y-3" data-testid="radar-axis-deltas">
            {Object.entries(FAMILY_LABELS).map(([key, label]) => {
              const score = subscores[key];
              const axisDelta = score != null ? Math.round((score - 50) * 10) / 10 : null;
              const colorClass = axisDelta == null
                ? 'text-cabinet-ink-soft'
                : axisDelta > 5 ? 'text-cabinet-forest'
                : axisDelta < -5 ? 'text-cabinet-bordeaux'
                : 'text-cabinet-ink-secondary';
              return (
                <li key={key} className="flex items-center justify-between border-b border-cabinet-border pb-2 last:border-b-0">
                  <span className="text-[12px] text-cabinet-ink">{label}</span>
                  <span className={`text-[10px] font-medium uppercase tracking-[0.18em] ${colorClass}`}>
                    {axisDelta != null ? formatPointsFr(axisDelta, 1) : '—'}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(FAMILY_LABELS).map(([key, label]) => {
          const score = subscores[key];
          return (
            <div key={key} className="bg-cabinet-paper border border-cabinet-border p-5" data-testid={`family-card-${key}`}>
              <div className="text-[9px] text-cabinet-ink-secondary uppercase tracking-[0.22em] mb-2">{label}</div>
              <div className="font-playfair text-[28px] text-cabinet-ink leading-none">
                {score != null ? formatNumberFr(score, 0) : '—'}
                <span className="text-[11px] text-cabinet-ink-soft ml-1">/100</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
